import { Drawer } from '@mantine/core';
import clsx from 'clsx';
import { isEqual, mapValues, noop } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useMeasure } from 'react-use';
import { darkModeRoot } from 'src/app/App.css';
import { FilterToolbarItemId } from 'src/components/Filters';
import { MobileHeaderBar } from 'src/components/mobile/MobileHeader';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { useMobileSalesSortAndFilterContext } from 'src/contexts/MobileSortAndFilterContext/MobileSalesSortAndFilterContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { vars } from 'src/core/themes';
import { globalMobileWrapper } from 'src/core/themes/themeContract.css';
import { Button, Radio, RadioGroup, Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import {
  DateRangePresetName,
  DateRangeRelativePreset,
  formatRelativeDateRange,
  getDateRangeRelativePresets,
} from 'src/utils/dateTimeUtils';

import { ActionToAllFilters } from './components/ActionToAllFilters';
import { EventSelectedFilters } from './components/InventorySelectedFilters';
import { InventoryCustomFilter } from './InventoryCustomFilterContent';
import * as styles from './InventoryListingSortingContent.css';

const OutstandingFilterItemIds: FilterToolbarItemId[] = ['eventDates'];

export const SaleEventFilterDrawer = ({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) => {
  const { isDarkMode } = useSiteTheme();
  const [headerRef, { height }] = useMeasure<HTMLElement>();

  const {
    onSaveFilterQuery,
    onResetFilters,
    tempQuery,
    selectedFilters,
    filters,
    canSaveFilter,
    quickFilters,
    onResetCustomFilter,
    onSaveSelectedFilters,
    onFilterRemoved,
  } = useMobileSalesSortAndFilterContext();

  const hasNonOutstandingFilters =
    useMemo(() => {
      const nonOutstandingFilters = mapValues(selectedFilters, (groupFilters) =>
        groupFilters.filter(
          (filter) => !OutstandingFilterItemIds.includes(filter.filterId)
        )
      );
      return Object.values(nonOutstandingFilters).flatMap((filters) => filters);
    }, [selectedFilters]).length > 0;

  const selectedFilterCount = useMemo(
    () => Object.values(selectedFilters).flatMap((filters) => filters).length,
    [selectedFilters]
  );

  return (
    <Drawer.Root
      zIndex={50}
      opened={show}
      onClose={onClose}
      size="100%"
      position="right"
      className={clsx(globalMobileWrapper, {
        [darkModeRoot]: isDarkMode,
      })}
    >
      <Drawer.Overlay />
      <Drawer.Content
        bg={vars.color.backgroundSecondary}
        style={{ overflowY: 'hidden', color: vars.color.textPrimary }}
        className={styles.drawerContent}
      >
        <Drawer.Header
          ref={headerRef}
          bg={vars.color.backgroundSecondary}
          className={styles.drawerHeader}
        >
          <MobileHeaderBar
            onBack={onClose}
            title={
              hasNonOutstandingFilters ? (
                <FormatContent
                  id={FormatContentId.NFiltersSelected}
                  params={selectedFilterCount + ''}
                />
              ) : (
                <Content id={ContentId.FilterEventsBy} />
              )
            }
            actions={
              !hasNonOutstandingFilters && (
                <InventoryCustomFilter
                  quickFilters={quickFilters}
                  onResetCustomFilter={onResetCustomFilter}
                />
              )
            }
          />
        </Drawer.Header>
        <Drawer.Body
          bg={vars.color.backgroundSecondary}
          style={{ height: `calc(100% - ${height + 30}px`, overflow: 'hidden' }}
          className={styles.drawerBody}
        >
          <Stack
            height="full"
            direction="column"
            gap="m"
            className={styles.drawerInnerContent}
          >
            <Stack
              direction="column"
              gap="l"
              style={{ flex: 1, overflowY: 'auto' }}
            >
              {hasNonOutstandingFilters ? (
                <EventSelectedFilters
                  tempQuery={tempQuery}
                  selectedDropdownItems={selectedFilters}
                  filters={filters}
                  onSaveSelectedFilters={onSaveSelectedFilters}
                  onFilterRemoved={onFilterRemoved}
                />
              ) : (
                <>
                  <EventFilterByEventDate />
                  <ActionToAllFilters
                    selectedFilters={selectedFilters}
                    filters={filters}
                    onSaveSelectedFilters={onSaveSelectedFilters}
                  />
                </>
              )}
            </Stack>
            <Stack
              justifyContent="spaceBetween"
              width="full"
              alignItems="center"
            >
              <Button
                shape="rect"
                variant="outlineGray"
                onClick={onResetFilters}
              >
                <Content id={ContentId.Clear} />
              </Button>
              <Button
                shape="rect"
                disabled={!canSaveFilter}
                onClick={onSaveFilterQuery}
              >
                <Content id={ContentId.Apply} />
              </Button>
            </Stack>
          </Stack>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
};

const EventDatePresets: DateRangePresetName[] = [
  DateRangePresetName.Yesterday,
  DateRangePresetName.Today,
  DateRangePresetName.NextWeek,
  DateRangePresetName.Next30Days,
];

const EventFilterByEventDate = () => {
  const presetEventDateFilters = getDateRangeRelativePresets(EventDatePresets);
  const { tempQuery, setTempQuery } = useMobileSalesSortAndFilterContext();

  const onUpdateRelativeDateRange = useCallback(
    (preset: DateRangeRelativePreset) => {
      const { range } = preset;
      setTempQuery({
        ...tempQuery,
        eventDates: range,
      });
    },
    [setTempQuery, tempQuery]
  );

  const selectedPreset = useMemo(() => {
    return presetEventDateFilters.find(({ range }) =>
      isEqual(range, tempQuery.eventDates)
    );
  }, [presetEventDateFilters, tempQuery.eventDates]);

  return (
    <Stack direction="column" gap="m">
      <span className={styles.subtitleAfter}>
        <Content id={ContentId.FilterByEventDate} />
      </span>
      <RadioGroup value={selectedPreset?.name} onValueChange={noop}>
        <Stack direction="column" gap="s">
          {presetEventDateFilters.map((preset) => (
            <InventoryEventDateSelectorItem
              key={preset.name}
              dateRangeRelativePreset={preset}
              onSelect={onUpdateRelativeDateRange}
            />
          ))}
        </Stack>
      </RadioGroup>
    </Stack>
  );
};

const InventoryEventDateSelectorItem = ({
  dateRangeRelativePreset,
  onSelect,
}: {
  dateRangeRelativePreset: DateRangeRelativePreset;
  onSelect: (dateItem: DateRangeRelativePreset) => void;
}) => {
  const { toZonedTime } = useSiteTimezoneContext();

  const onSelectRelativePreset = useCallback(() => {
    onSelect(dateRangeRelativePreset);
  }, [dateRangeRelativePreset, onSelect]);
  return (
    <Stack
      role="button"
      width="full"
      className={styles.inventoryFilterGroupItem}
      style={{ cursor: 'pointer' }}
      justifyContent="spaceBetween"
      onClick={onSelectRelativePreset}
    >
      <Radio
        value={dateRangeRelativePreset.name}
        label={dateRangeRelativePreset.label}
        onChange={onSelectRelativePreset}
      />
      <span className={styles.sotringOrderDescription}>
        {formatRelativeDateRange(dateRangeRelativePreset.range, toZonedTime)}
      </span>
    </Stack>
  );
};
